import { useState } from "react";
import { ListTodo } from "lucide-react";

import { IconMyClimateActions } from "@/components/_icons/IconMyClimateActions";
import { ButtonHelp } from "@/components/_molecules/button-help";
import { Sidebar } from "@/components/_templates/sidebar/sidebar";
import { SidebarBottomContent } from "@/components/_templates/sidebar/sidebar-bottom-content";
import { SidebarJoinTeamButton } from "@/components/_templates/sidebar/sidebar-join-team-button";
import { SidebarMainContent } from "@/components/_templates/sidebar/sidebar-main-content";
import { SidebarNavElement } from "@/components/_templates/sidebar/sidebar-nav-element";
import { SidebarSectionToggle } from "@/components/_templates/sidebar/sidebar-section-toggle";
import { SidebarTopContent } from "@/components/_templates/sidebar/sidebar-top-content";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { ProjectIcon } from "@/domain/project/components/project-icons";
import { SidebarAppFavorites } from "@/domain/user/components/sidebar-app-favorites";
import { CurrentUser } from "@/domain/user/models/current-user-model";
import { useCanManageWorkspace } from "@/domain/workspace/hooks/use-can-manage-workspace";
import { useWorkspaceTerminology } from "@/domain/workspace/hooks/use-workspace-terminology";
import { makeWorkspaceSettingsUrl, makeWorkspaceUrl } from "@/domain/workspace/lib/make-workspace-url";
import { Workspace } from "@/domain/workspace/models/workspace-model";
import { PlusIcon } from "@heroicons/react/24/solid";

import { SidebarAppAllTeamsNavElement } from "./components/sidebar-app-all-teams-nav-element";
import { SidebarAppWorkspaceElement } from "./components/sidebar-app-workspace-element";
import { UserMenuDropdown } from "./components/user-menu-dropdown";
import { WorkspaceSwitcherDropdown } from "./components/workspace-switcher-dropdown";

interface SidebarAppProps {
  workspace: Workspace;
  user: CurrentUser;
}

export const SidebarApp = ({ workspace, user }: SidebarAppProps) => {
  const [isMyAreaExpanded, setIsMyAreaExpanded] = useState(true);
  const { t } = useDomainTranslation("common");
  const terminology = useWorkspaceTerminology(workspace.id);
  const { canManageWorkspace } = useCanManageWorkspace({ workspaceId: workspace.id });

  return (
    <Sidebar>
      <SidebarTopContent>
        <WorkspaceSwitcherDropdown workspace={workspace} user={user} />
        <div className="flex flex-initial grow flex-row text-xs" style={{ minWidth: "8px" }}></div>
        <UserMenuDropdown workspace={workspace} user={user} />
      </SidebarTopContent>

      <SidebarMainContent>
        <SidebarSectionToggle
          label={t("sidebar.myArea")}
          expanded={isMyAreaExpanded}
          onToggle={() => {
            setIsMyAreaExpanded(!isMyAreaExpanded);
          }}
          ariaControls="my-area-panel"
        />
        {isMyAreaExpanded && (
          <div id="my-area-panel" role="region" aria-label={t("sidebar.myArea")}>
            <SidebarNavElement to={makeWorkspaceUrl({ workspace, path: "my-tasks" })}>
              <ListTodo className="mr-2 h-3 w-3" />
              {t("sidebar.myTasks")}
            </SidebarNavElement>
            <SidebarNavElement to={makeWorkspaceUrl({ workspace, path: "my-projects" })}>
              <ProjectIcon className="mr-2 h-3 w-3" />
              {t("sidebar.myProjects")}
            </SidebarNavElement>
            <SidebarNavElement to={makeWorkspaceUrl({ workspace, path: "my-climate-actions" })}>
              <IconMyClimateActions className="mr-2 h-3 w-3" />
              {t("sidebar.myClimateActions", { climateActionPlural: terminology.climateAction.plural })}
            </SidebarNavElement>
          </div>
        )}

        <SidebarAppFavorites />

        <div className="mt-4" />
        <SidebarAppWorkspaceElement />

        <div className="mt-4" />

        {user && (
          <>
            <SidebarAppAllTeamsNavElement workspace={workspace} user={user} />

            <div className="mt-4" />

            <SidebarJoinTeamButton workspaceId={workspace.id} userId={user.id} />

            <div className="mt-1" />

            {canManageWorkspace && (
              <SidebarNavElement to={makeWorkspaceSettingsUrl({ workspace, path: "new-team" })}>
                <PlusIcon className="mr-2 h-3.5 w-3.5" />
                {t("sidebar.addTeam")}
              </SidebarNavElement>
            )}
          </>
        )}
      </SidebarMainContent>

      <SidebarBottomContent>
        <ButtonHelp />
      </SidebarBottomContent>
    </Sidebar>
  );
};
